import React, { useContext, useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import useAdminNotifications from '@/hooks/useAdminNotifications';
import { 
  LayoutDashboard, Users, UserCheck, Package, CreditCard, Wallet,
  DollarSign, Calendar, Star, Headphones, AlertTriangle, Settings,
  LogOut, Car, ChevronRight, Shield, FileText, Send, BarChart3, Trophy,
  User, KeyRound, ChevronDown
} from 'lucide-react';

const menuItems = [
  { 
    section: 'Principal',
    items: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/control-portal-clube' },
    ]
  },
  {
    section: 'Usuários',
    items: [
      { icon: Users, label: 'Clientes', path: '/control-portal-clube/clients' },
      { icon: UserCheck, label: 'Prestadores', path: '/control-portal-clube/providers' },
      { icon: Trophy, label: 'Score Prestadores', path: '/control-portal-clube/provider-scores' },
    ]
  },
  {
    section: 'Negócios',
    items: [
      { icon: Package, label: 'Planos', path: '/control-portal-clube/plans' },
      { icon: CreditCard, label: 'Assinaturas', path: '/control-portal-clube/subscriptions' },
      { icon: Calendar, label: 'Agendamentos', path: '/control-portal-clube/appointments' },
    ]
  },
  {
    section: 'Financeiro',
    items: [
      { icon: BarChart3, label: 'Dashboard', path: '/control-portal-clube/financial-dashboard' },
      { icon: Wallet, label: 'Visão Geral', path: '/control-portal-clube/financial' },
      { icon: DollarSign, label: 'Repasses', path: '/control-portal-clube/transfers' },
      { icon: Send, label: 'Solicitações', path: '/control-portal-clube/payout-requests' },
      { icon: Settings, label: 'Config. Repasse', path: '/control-portal-clube/financial-settings' },
    ]
  },
  {
    section: 'Qualidade',
    items: [
      { icon: Star, label: 'Avaliações', path: '/control-portal-clube/reviews' },
      { icon: Headphones, label: 'Suporte', path: '/control-portal-clube/support' },
      { icon: AlertTriangle, label: 'Contestações', path: '/control-portal-clube/disputes' },
      { icon: Shield, label: 'Antifraude', path: '/control-portal-clube/fraud' },
    ]
  },
  {
    section: 'Sistema',
    items: [
      { icon: Settings, label: 'Configurações', path: '/control-portal-clube/settings' },
      { icon: FileText, label: 'Termos de Uso', path: '/control-portal-clube/terms' },
    ]
  }
];

export default function AdminLayout({ children, title, subtitle }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useContext(AuthContext);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);

  // PROMPT BLINDADO: central operacional — polling leve 25s sobre módulos críticos
  const notif = useAdminNotifications();

  // Mapeamento path → contador (badge na sidebar)
  const badgeCounts = {
    '/control-portal-clube/support':          notif.support,
    '/control-portal-clube/disputes':         notif.disputes,
    '/control-portal-clube/fraud':            notif.fraud,
    '/control-portal-clube/financial':        notif.pendingPayments,
    '/control-portal-clube/subscriptions':    notif.expiringSubs,
    '/control-portal-clube/clients':          notif.unverified,
  };

  // Fechar menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getInitials = (name) => {
    if (!name) return 'AD';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Sidebar - Azul profundo (#0B1F3A) */}
      <aside className="w-64 bg-[#0B1F3A] text-white flex flex-col fixed h-full z-20">
        {/* Logo */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="bg-[#2563EB] p-2 rounded-lg">
              <Car className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg">JATOO</h1>
              <p className="text-xs text-white/60">Painel Admin</p>
            </div>
          </div>
        </div>

        {/* Menu */}
        <ScrollArea className="flex-1 py-4">
          <nav className="px-3 space-y-6">
            {menuItems.map((section, idx) => (
              <div key={idx}>
                <p className="px-3 text-xs font-semibold text-white/40 uppercase tracking-wider mb-2">
                  {section.section}
                </p>
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path || 
                      (item.path !== '/control-portal-clube' && location.pathname.startsWith(item.path));
                    const badge = badgeCounts[item.path] || 0;
                    
                    return (
                      <button
                        key={item.path}
                        onClick={() => navigate(item.path)}
                        data-testid={`admin-nav-${item.path.replace(/\//g, '-')}`}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                          isActive 
                            ? 'bg-[#2563EB] text-white shadow-lg shadow-blue-500/25' 
                            : 'text-white/70 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span>{item.label}</span>
                        {badge > 0 && (
                          <span
                            data-testid={`admin-nav-badge-${item.path.replace(/\//g, '-')}`}
                            className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-[10px] font-bold leading-none bg-red-500 text-white"
                          >
                            {badge > 99 ? '99+' : badge}
                          </span>
                        )}
                        {badge === 0 && isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
        </ScrollArea>

        {/* User Section with Dropdown */}
        <div className="p-4 border-t border-white/10" ref={profileMenuRef}>
          <div className="relative">
            {/* Profile Button */}
            <button
              onClick={() => setProfileMenuOpen(!profileMenuOpen)}
              className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/10 transition-colors"
              data-testid="admin-profile-btn"
            >
              <div className="w-10 h-10 rounded-full bg-[#2563EB] flex items-center justify-center text-white font-semibold flex-shrink-0">
                {getInitials(user?.name)}
              </div>
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-medium truncate">{user?.name || 'Admin'}</p>
                <p className="text-xs text-white/50 truncate">{user?.email}</p>
              </div>
              <ChevronDown className={`w-4 h-4 text-white/50 transition-transform ${profileMenuOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Dropdown Menu */}
            {profileMenuOpen && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-[#1a3354] rounded-lg shadow-xl border border-white/10 overflow-hidden z-50">
                <button
                  onClick={() => {
                    setProfileMenuOpen(false);
                    navigate('/control-portal-clube/change-password');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-white/80 hover:bg-white/10 hover:text-white transition-colors"
                  data-testid="admin-change-password-btn"
                >
                  <KeyRound className="w-4 h-4" />
                  <span className="text-sm">Alterar senha</span>
                </button>
                <div className="border-t border-white/10" />
                <button
                  onClick={() => {
                    setProfileMenuOpen(false);
                    handleLogout();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
                  data-testid="admin-logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="text-sm">Sair</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64">
        {/* Header */}
        {(title || subtitle) && (
          <header className="bg-white border-b border-[#E2E8F0] px-6 py-4 sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <div>
                {title && <h1 className="text-xl font-bold text-[#0B1F3A]">{title}</h1>}
                {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0B1F3A]/5 text-sm text-[#0B1F3A]">
                  <Shield className="w-4 h-4" />
                  <span className="font-medium">Modo Admin</span>
                </div>
              </div>
            </div>
          </header>
        )}

        {/* Page Content */}
        <div className="p-6">
          {children}
        </div>
      </main>
    </div>
  );
}
